import type { FC, FormEvent, ReactNode } from 'react'
import type { AttendanceStatus, JournalEntry } from '@/types'

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { compareText } from '@/lib/academic'
import { DEMO_TODAY, formatDate } from '@/lib/dates'
import { Seo } from '@/components/generals/seo'
import { Badge } from '@/components/ui/badge'
import { Button, ButtonLink } from '@/components/ui/button'
import { Card, CardHeader } from '@/components/ui/card'
import { Field } from '@/components/ui/field'
import { Input, Textarea } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { PageHeader } from '@/components/app/page-header'
import { PageSkeleton } from '@/components/app/page-skeleton'
import { STATUS_TONE } from '@/components/app/status'
import { useAcademic } from '@/components/providers/academic-provider'
import { useConfirm } from '@/components/providers/confirm-provider'
import { useToast } from '@/components/providers/toast-provider'
import { useQueryParams } from '@/hooks/use-query-params'
import { ATTENDANCE_STATUSES } from '@/constants/academic'

import {
    THead,
    TBody,
    TD,
    TH,
    TR,
    Table
} from '@/components/ui/table'

import {
    ArrowRight,
    CalendarRange,
    Clock,
    NotebookPen,
    Pencil,
    Save,
    Trash2
} from 'lucide-react'

// The journal page labels the fourth status "Alpha", as in the original app
const JOURNAL_LABELS: Record<AttendanceStatus, string> = {
    H: 'Hadir',
    S: 'Sakit',
    I: 'Izin',
    A: 'Alpha'
}

const STAT_DOTS: Record<AttendanceStatus, string> = {
    H: 'bg-success',
    S: 'bg-warning',
    I: 'bg-info',
    A: 'bg-destructive'
}

const periodOrder = (period: string): number => Number(period.split('-')[0]) || 0

type JournalFormProps = {
    classId: string
    date: string
    editing: JournalEntry | null
    onDone: () => void
}

const JournalForm: FC<JournalFormProps> = ({ classId, date, editing, onDone }): ReactNode => {
    const { actions } = useAcademic()
    const toast = useToast()

    const [period, setPeriod] = useState(editing?.period ?? '')
    const [start, setStart] = useState(editing?.start ?? '07:00')
    const [end, setEnd] = useState(editing?.end ?? '07:40')
    const [material, setMaterial] = useState(editing?.material ?? '')

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()

        const result = actions.saveJournal({ id: editing?.id, classId, date, period, start, end, material })

        if (!result.ok) {
            toast.error('Gagal menyimpan', result.error)

            return
        }

        toast.success(result.message ?? 'Jurnal mengajar berhasil disimpan.')

        if (!editing) {
            setPeriod('')
            setMaterial('')
        }

        onDone()
    }

    return (
        <form onSubmit={handleSubmit} className={cn('grid gap-4 border-b p-5 lg:grid-cols-[15rem_minmax(0,1fr)_auto]', editing && 'bg-primary/5')}>
            <div className='space-y-3'>
                <Field label='Jam keberapa' htmlFor='journalPeriod'>
                    <Input
                        id='journalPeriod'
                        value={period}
                        onChange={event => setPeriod(event.target.value)}
                        maxLength={5}
                        placeholder='Mis. 1–2'
                        required
                    />
                </Field>

                <div className='grid grid-cols-2 gap-2'>
                    <Field label='Mulai' htmlFor='journalStart'>
                        <Input id='journalStart' type='time' value={start} onChange={event => setStart(event.target.value)} required />
                    </Field>

                    <Field label='Selesai' htmlFor='journalEnd'>
                        <Input id='journalEnd' type='time' value={end} onChange={event => setEnd(event.target.value)} required />
                    </Field>
                </div>
            </div>

            <Field label='Materi pembelajaran' htmlFor='journalMaterial'>
                <Textarea
                    id='journalMaterial'
                    value={material}
                    onChange={event => setMaterial(event.target.value)}
                    maxLength={2000}
                    placeholder='Tulis materi pembelajaran...'
                    className='min-h-32 lg:min-h-[8.75rem]'
                    required
                />
            </Field>

            <div className='flex gap-2 lg:flex-col lg:justify-end'>
                <Button type='submit' className='flex-1 lg:flex-none'>
                    <Save aria-hidden='true' />
                    {editing ? 'Simpan perubahan' : 'Simpan'}
                </Button>

                {editing && (
                    <Button variant='outline' onClick={onDone} className='flex-1 lg:flex-none'>
                        Batal
                    </Button>
                )}
            </div>
        </form>
    )
}

const JournalPage: FC = (): ReactNode => {
    const { classes, students, attendance, journals, classById, actions } = useAcademic()
    const { ready, get, set } = useQueryParams()
    const confirm = useConfirm()
    const toast = useToast()

    if (!ready) return <PageSkeleton />

    const editing = journals.find(item => item.id === get('edit')) ?? null
    const requestedClass = editing?.classId ?? get('kelas')
    const classId = classes.some(item => item.id === requestedClass) ? requestedClass as string : classes[0]?.id ?? ''
    const requestedDate = editing?.date ?? get('tanggal') ?? ''
    const date = /^\d{4}-\d{2}-\d{2}$/.test(requestedDate) ? requestedDate : DEMO_TODAY
    const className = classById.get(classId)?.name ?? ''

    const rows = journals
        .filter(item => item.classId === classId && item.date === date)
        .sort((a, b) => periodOrder(a.period) - periodOrder(b.period) || compareText(a.period, b.period))

    const classStudents = students.filter(student => student.classId === classId)
    const statusByStudent = new Map(attendance.filter(record => record.classId === classId && record.date === date).map(record => [record.studentId, record.status]))
    const counts: Record<AttendanceStatus, number> = { H: 0, S: 0, I: 0, A: 0 }

    classStudents.forEach(student => {
        const status = statusByStudent.get(student.id)

        if (status) counts[status] += 1
    })

    const handleDelete = async (entry: JournalEntry): Promise<void> => {
        const approved = await confirm({
            title: 'Hapus catatan jurnal?',
            description: `Jurnal jam ke ${entry.period} pada ${formatDate(entry.date, 'short')} akan dihapus.`,
            confirmLabel: 'Ya, hapus'
        })

        if (!approved) return

        const result = actions.deleteJournal(entry.id)

        if (result.ok) toast.success(result.message ?? 'Catatan jurnal dihapus.')
        if (editing?.id === entry.id) set({ edit: null })
    }

    return (
        <>
            <Seo title='Jurnal mengajar' />

            <PageHeader
                eyebrow='Kegiatan pembelajaran'
                title='Jurnal mengajar'
                description='Catat nomor absen, jam pelajaran, dan materi. Daftar siswa serta status kehadiran tersinkron otomatis dari menu Absen.'
                actions={(
                    <>
                        <ButtonLink href={`/rekap?view=jurnal&kelas=${classId}&bulan=${date.slice(0, 7)}`} variant='outline' size='sm'>
                            <CalendarRange aria-hidden='true' />
                            Rekap jurnal bulanan
                        </ButtonLink>

                        <ButtonLink href={`/absen?kelas=${classId}&tanggal=${date}`} variant='outline' size='sm'>
                            Buka absensi
                            <ArrowRight aria-hidden='true' />
                        </ButtonLink>
                    </>
                )}
            />

            <Card className='mb-6 grid gap-4 p-4 sm:grid-cols-2 sm:p-5 lg:max-w-2xl'>
                <Field label='Kelas' htmlFor='journalClass'>
                    <Select id='journalClass' value={classId} onChange={event => set({ kelas: event.target.value, edit: null })}>
                        {classes.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                    </Select>
                </Field>

                <Field label='Tanggal' htmlFor='journalDate'>
                    <Input
                        id='journalDate'
                        type='date'
                        value={date}
                        onChange={event => {
                            if (event.target.value) set({ tanggal: event.target.value, kelas: classId, edit: null })
                        }}
                    />
                </Field>
            </Card>

            <div className='grid items-start gap-6 2xl:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]'>
                <Card>
                    <CardHeader
                        icon={editing ? <Pencil /> : <NotebookPen />}
                        title={editing ? 'Ubah catatan jurnal' : 'Jurnal mengajar'}
                        description={`${className} · ${formatDate(date, 'short')}`}
                        action={<Badge tone='primary'>{rows.length} catatan</Badge>}
                    />

                    <JournalForm
                        key={editing?.id ?? `baru-${classId}-${date}`}
                        classId={classId}
                        date={date}
                        editing={editing}
                        onDone={() => set({ edit: null, kelas: classId, tanggal: date })}
                    />

                    {rows.length > 0 ? (
                        <ul className='divide-y'>
                            {rows.map(entry => (
                                <li key={entry.id} className={cn('flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-start', editing?.id === entry.id && 'bg-primary/5')}>
                                    <span className='inline-flex shrink-0 items-center gap-1.5 self-start rounded-lg border bg-muted/50 px-2.5 py-1 text-xs font-semibold text-foreground tabular-nums'>
                                        <Clock className='size-3.5 text-primary' aria-hidden='true' />
                                        {entry.period} · {entry.start}–{entry.end}
                                    </span>

                                    <p className='min-w-0 flex-1 text-sm leading-relaxed whitespace-pre-line text-foreground'>
                                        {entry.material}
                                    </p>

                                    <div className='flex shrink-0 gap-2'>
                                        <Button variant='outline' size='xs' onClick={() => set({ edit: entry.id })}>
                                            <Pencil aria-hidden='true' />
                                            Ubah
                                        </Button>

                                        <Button variant='danger-outline' size='xs' onClick={() => void handleDelete(entry)}>
                                            <Trash2 aria-hidden='true' />
                                            Hapus
                                        </Button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className='px-5 py-8 text-center text-[13px] text-muted-foreground'>
                            Belum ada jurnal tersimpan untuk tanggal ini. Isi baris di atas, lalu tekan Simpan.
                        </p>
                    )}
                </Card>

                <Card>
                    <CardHeader
                        title='Absensi siswa'
                        description='Sakit, izin, atau alpha diberi keterangan; siswa hadir atau belum diabsen ditandai —.'
                        action={(
                            <ButtonLink href={`/absen?kelas=${classId}&tanggal=${date}`} variant='outline' size='sm'>
                                Kelola absensi
                            </ButtonLink>
                        )}
                    />

                    <div className='grid grid-cols-4 divide-x border-b'>
                        {ATTENDANCE_STATUSES.map(status => (
                            <div key={status} className='px-3 py-3.5 text-center'>
                                <p className='flex items-center justify-center gap-1.5 text-xs text-muted-foreground'>
                                    <span className={cn('size-1.5 rounded-full', STAT_DOTS[status])} aria-hidden='true' />
                                    {JOURNAL_LABELS[status]}
                                </p>

                                <p className='mt-1 text-xl font-bold text-foreground'>
                                    {counts[status]}
                                </p>
                            </div>
                        ))}
                    </div>

                    <Table>
                        <THead>
                            <tr>
                                <TH className='w-24'>No. Absen</TH>
                                <TH>Siswa</TH>
                                <TH className='text-center'>Keterangan</TH>
                            </tr>
                        </THead>

                        <TBody>
                            {classStudents.map((student, index) => {
                                const status = statusByStudent.get(student.id)

                                return (
                                    <TR key={student.id}>
                                        <TD className='font-semibold text-muted-foreground tabular-nums'>{index + 1}</TD>
                                        <TD>
                                            <p className='font-semibold text-foreground'>{student.name}</p>
                                            <p className='text-xs text-muted-foreground'>NIS {student.nis}</p>
                                        </TD>
                                        <TD className='text-center'>
                                            {status ? (
                                                <Badge tone={STATUS_TONE[status]} dot>{JOURNAL_LABELS[status]}</Badge>
                                            ) : (
                                                <span className='text-muted-foreground'>—</span>
                                            )}
                                        </TD>
                                    </TR>
                                )
                            })}

                            {classStudents.length === 0 && (
                                <tr>
                                    <td colSpan={3} className='px-5 py-10 text-center text-[13px] text-muted-foreground'>
                                        Belum ada siswa yang terdaftar di kelas ini.
                                    </td>
                                </tr>
                            )}
                        </TBody>
                    </Table>
                </Card>
            </div>
        </>
    )
}

export default JournalPage
